'use client';
import React, { useMemo, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';
import Button from '@src/v3/components/coreUI/button';
import TextField from '@src/v3/components/coreUI/textField';
import Icon from '@src/v3/components/coreUI/icon';
import { DialogHeader } from '@src/v3/components/coreUI/dialog';
import CountryCodeSelect from '@src/v3/components/coreUI/countryCodeSelect';
import GoogleLoginButton from '@src/v3/components/ssoLogin/loginWithGoogle';
import AppleButton from '@src/v3/components/ssoLogin/loginWithApple';
import GradientPanelSvg from '@src/v3/assets/svg/gradientPanel';
import {
  LoginStepProps,
  LoginFormValues,
} from '@src/v3/interfaces/components/loginModal';
import { initialValues } from '@src/v3/schemas/components/login';
import {
  getIso2FromDialCode,
  getPhoneMask,
  formatPhoneDisplayForCountry,
} from '@src/v3/utils/helper';
import { ERROR_MESSAGES } from '@src/v3/constants/messages';
import { jwtDecode } from 'jwt-decode';

const PhoneStep: React.FC<LoginStepProps> = ({
  onPhoneSubmit,
  isLoading = false,
  onGoogleOAuth,
  onAppleOAuth,
  onStepChange: _onStepChange, // eslint-disable-line @typescript-eslint/no-unused-vars, no-unused-vars
  title = 'Login',
  hideOAuth = false,
  onClose,
  onOpenLoginModal,
  onOpenRegisterModal,
}) => {
  const isLoginMode = title === 'Login';
  const [isGoogleLoading, setIsGoogleLoading] = useState<boolean>(false);
  const [isAppleLoading, setIsAppleLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isAccountRegistered, setIsAccountRegistered] =
    useState<boolean>(false);
  const [selectedDialCode, setSelectedDialCode] = useState<string>('+1');

  const selectedIso2 = useMemo(
    () => getIso2FromDialCode(selectedDialCode),
    [selectedDialCode]
  );

  const phoneMask = useMemo(() => getPhoneMask(selectedIso2), [selectedIso2]);

  const phoneValidationSchema = useMemo(() => {
    return Yup.object({
      phone: Yup.string()
        .required('Phone number is required')
        .test('phone-format', 'Please enter valid phone number.', (value) => {
          const v = value || '';
          const parsed = parsePhoneNumberFromString(
            v,
            selectedIso2 as CountryCode
          );
          return !!parsed && parsed.isPossible();
        }),
    });
  }, [selectedIso2]);

  const canContinueWithPhone = (phone: string) => {
    const parsed = parsePhoneNumberFromString(
      phone || '',
      selectedIso2 as CountryCode
    );
    return !!parsed && parsed.isPossible();
  };

  const handleGoogleSuccess = async (token: string) => {
    setIsGoogleLoading(true);
    try {
      const response = await fetch(
        `https://www.googleapis.com/oauth2/v2/userinfo?access_token=${token}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch user info from Google');
      }
      const userInfo = await response.json();
      if (!userInfo.email) {
        throw new Error('No email received from Google');
      }
      await onGoogleOAuth?.(token, {
        name: userInfo.name || '',
        email: userInfo.email,
        picture: userInfo.picture || '',
      });
    } catch (error) {
      console.error('Failed to fetch Google user info:', error);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleAppleSuccess = async (
    token: string,
    user: { name?: { firstName?: string; lastName?: string }; email?: string }
  ) => {
    setIsAppleLoading(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = jwtDecode(token);
      const userEmail = res?.email || user?.email || '';

      const userInfo = {
        name: user?.name
          ? {
              firstName: user.name.firstName || '',
              lastName: user.name.lastName || '',
            }
          : undefined,
        email: userEmail,
      };
      await onAppleOAuth?.(token, userInfo);
    } catch (error) {
      console.error('Failed to initiate Apple OAuth:', error);
    } finally {
      setIsAppleLoading(false);
    }
  };

  const handleSubmit = async (
    values: LoginFormValues,
    // eslint-disable-next-line no-unused-vars
    { setSubmitting }: { setSubmitting: (_isSubmitting: boolean) => void }
  ) => {
    if (!values.phone || values.phone.trim() === '') {
      setSubmitting(false);
      return;
    }
    const parsed = parsePhoneNumberFromString(
      values.phone,
      selectedIso2 as CountryCode
    );
    if (!parsed || !parsed.isPossible()) {
      setSubmitting(false);
      return;
    }

    setErrorMessage(''); // Clear previous error
    try {
      await onPhoneSubmit?.({
        ...values,
        // Submit E.164 (handles trunk prefixes like leading 0 automatically)
        phone: parsed.number,
      });
    } catch (error) {
      const apiError = error as {
        response?: {
          data?: {
            message?: string;
            is_account_registered?: boolean;
          };
        };
        message?: string;
        is_account_registered?: boolean;
      };
      const errorMsg =
        apiError?.response?.data?.message || apiError?.message || '';
      setIsAccountRegistered(
        apiError?.response?.data?.is_account_registered ||
          apiError?.is_account_registered ||
          false
      );
      setErrorMessage(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };
  const handleToggleModal = () => {
    if (isLoginMode) {
      if (onOpenRegisterModal) {
        onOpenRegisterModal();
      } else {
        onClose();
      }
    } else {
      if (onOpenLoginModal) {
        onOpenLoginModal();
      } else {
        onClose();
      }
    }
  };

  const handleContinue = (
    submitForm: () => void,
    isValid: boolean,
    values: LoginFormValues
  ) => {
    if (isValid && values.phone) {
      submitForm();
    }
  };

  const handlePhoneChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    // eslint-disable-next-line no-unused-vars
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  ) => {
    handleChange(e);
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  // When country is not US, allow paste of numbers with spaces by normalizing
  const handlePhonePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    // eslint-disable-next-line no-unused-vars
    setFieldValue: (field: string, value: string) => void
  ) => {
    if (selectedIso2 === 'US') return;
    const pasted = e.clipboardData.getData('text');
    const normalized = pasted.replace(/[^\d+]/g, '');
    if (normalized !== pasted) {
      e.preventDefault();
      setFieldValue('phone', normalized);
    }
  };

  return (
    <div className="h-full flex flex-col md:flex-row">
      {/* Left Section - White Panel */}
      <div className="md:w-[686px] w-full h-full md:h-[790px] self-stretch px-0 md:px-2.5 pt-0 md:pt-2.5 pb-0 md:pb-8 bg-white rounded-none md:rounded-l-2xl flex flex-col items-start relative">
        {/* Mobile Header */}
        <div className="md:hidden w-full">
          <DialogHeader className="px-5 pt-6 pb-5 flex flex-col gap-3 items-start">
            <div className="self-stretch flex flex-col justify-start items-start gap-1">
              <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                <div className="flex-1 justify-start text-black text-2xl font-medium font-geist leading-9">
                  {isLoginMode ? 'Login' : 'Sign up'}
                </div>
                <span
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <Icon
                    iconName="crossicon"
                    iconProps={{ className: 'w-5 h-5 text-gray-600' }}
                  />
                </span>
              </div>
              <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                <div className="flex-1 justify-start text-gray-800 text-base font-normal font-geist leading-6">
                  {isLoginMode
                    ? 'Glad to see you again!'
                    : 'Join our community'}
                </div>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Desktop Header */}
        <div className="hidden md:flex self-stretch  flex-col justify-start items-start gap-2">
          <div className="self-stretch px-8 pt-16 pb-5 bg-white inline-flex flex-col justify-center items-start gap-3">
            <div className="self-stretch inline-flex flex-col justify-start items-start gap-4">
              <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                <div className="flex-1 justify-start text-gray-1000 text-xl md:text-2xl font-medium font-geist leading-7 md:leading-9">
                  {isLoginMode ? 'Login!' : 'Welcome to Zeme'}
                </div>
              </div>
              <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                <div className="flex-1 justify-start text-gray-1000 text-sm md:text-base font-normal font-geist leading-6">
                  {isLoginMode
                    ? 'Glad to see you again!'
                    : 'Join our community'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Formik
          initialValues={{ ...initialValues, phone: initialValues.phone }}
          validationSchema={phoneValidationSchema}
          onSubmit={handleSubmit}
          validateOnMount={false}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting,
            submitForm,
            isValid,
            setFieldValue,
          }) => (
            <>
              <div className="flex-1 w-full flex flex-col overflow-y-auto md:pb-0">
                {/* Mobile Content - Login Mode */}
                {isLoginMode && (
                  <div className="md:hidden px-5 py-1 flex flex-col justify-center items-start gap-6">
                    <div className="self-stretch flex flex-col justify-end items-center gap-2.5">
                      <div className="self-stretch flex flex-col justify-start items-start gap-2">
                        <div className="self-stretch justify-start text-gray-800 text-sm font-normal font-geist leading-6">
                          Login
                        </div>
                        <TextField
                          placeholder="Phone Number"
                          mask={phoneMask}
                          size="lg"
                          type="tel"
                          maxLength={selectedIso2 !== 'US' ? 15 : undefined}
                          numericOnly={selectedIso2 !== 'US'}
                          countryCode={
                            <CountryCodeSelect
                              value={selectedDialCode}
                              onChange={(dialCode) => {
                                setSelectedDialCode(dialCode);
                                setFieldValue(
                                  'phone',
                                  formatPhoneDisplayForCountry(
                                    values.phone,
                                    dialCode
                                  )
                                );
                              }}
                              disabled={isLoading || isSubmitting}
                            />
                          }
                          error={
                            (touched.phone && !!errors.phone) || !!errorMessage
                          }
                          errorMessage={touched.phone ? errors.phone : ''}
                          onChange={(e) => handlePhoneChange(e, handleChange)}
                          onPaste={(e) => handlePhonePaste(e, setFieldValue)}
                          onBlur={handleBlur}
                          value={values.phone}
                          name="phone"
                          disabled={isLoading || isSubmitting}
                          tabIndex={-1}
                          containerClassName="self-stretch"
                          inputClassName="rounded-xl"
                        />
                      </div>

                      {errorMessage && (
                        <div className="w-full font-geist font-normal text-[14px] leading-[24px] tracking-[-0.14px] flex justify-start items-start gap-2">
                          <span className="flex items-start gap-2">
                            <Icon
                              iconName="warningCircle"
                              iconProps={{
                                className: 'w-5 h-5 flex-shrink-0 mt-0.5',
                              }}
                            />
                            <span className="text-[#E5484D] ">
                              {errorMessage}{' '}
                              {!isAccountRegistered && (
                                <span
                                  onClick={handleToggleModal}
                                  className="text-focus text-sm font-normal font-geist leading-6 hover:opacity-75 cursor-pointer h-auto p-0 underline"
                                >
                                  Sign up instead
                                </span>
                              )}
                            </span>
                          </span>
                        </div>
                      )}
                      <div className="self-stretch opacity-50 justify-start text-gray-1000 text-sm font-normal font-geist leading-6">
                        We&apos;ll text you to confirm your number. Standard
                        carrier rates may apply.
                      </div>
                    </div>
                    {/* Continue Button - Mobile Login */}
                    <Button
                      type="button"
                      variant="primary"
                      size="xl"
                      className="self-stretch h-12 px-4 py-3 bg-focus rounded-[36px] inline-flex justify-center items-center gap-2"
                      disabled={
                        isLoading ||
                        isSubmitting ||
                        isGoogleLoading ||
                        isAppleLoading ||
                        !values.phone ||
                        !canContinueWithPhone(values.phone)
                      }
                      isLoading={
                        isLoading ||
                        isSubmitting ||
                        isGoogleLoading ||
                        isAppleLoading
                      }
                      loadingType="dots"
                      onClick={() =>
                        handleContinue(submitForm, isValid, values)
                      }
                    >
                      <div className="justify-start text-white text-base font-medium font-geist leading-7">
                        Continue
                      </div>
                      <Icon
                        iconName="arrowRight"
                        iconProps={{ className: 'w-5 h-5 text-white' }}
                      />
                    </Button>

                    {/* Social Login Buttons - Mobile */}
                    {!hideOAuth && (
                      <>
                        <div className="self-stretch flex items-center gap-4">
                          <div className="flex-1 h-px bg-gray-300" />
                          <span className="text-gray-400 text-sm font-medium leading-none uppercase">
                            OR
                          </span>
                          <div className="flex-1 h-px bg-gray-300" />
                        </div>

                        <div className="flex flex-col gap-2 w-full">
                          <GoogleLoginButton
                            onSuccess={handleGoogleSuccess}
                            isLoading={isGoogleLoading}
                            disabled={isAppleLoading}
                          />
                          <AppleButton
                            onSuccess={(token, user) =>
                              handleAppleSuccess(token, user)
                            }
                            isLoading={isAppleLoading}
                            disabled={isGoogleLoading}
                          />
                        </div>
                      </>
                    )}

                    {/* Sign Up Link - Mobile Login */}
                    <div className="w-full justify-start text-focus text-sm font-normal font-geist leading-6">
                      <Button
                        type="button"
                        variant="link"
                        className="hover:opacity-85 h-auto p-0 !text-focus !text-sm"
                        onClick={handleToggleModal}
                      >
                        Don&apos;t have an account? Sign up
                      </Button>
                    </div>
                  </div>
                )}

                {/* Mobile Content - Signup Mode */}
                {!isLoginMode && (
                  <div className="md:hidden px-5 py-1 flex flex-col justify-center items-start gap-6">
                    <div className="self-stretch flex flex-col justify-end items-center gap-2.5">
                      <div className="self-stretch flex flex-col justify-start items-start gap-2">
                        <div className="self-stretch justify-start text-gray-800 text-sm font-normal font-geist leading-6">
                          Create your account
                        </div>
                        <TextField
                          placeholder="Phone Number"
                          mask={phoneMask}
                          size="lg"
                          type="tel"
                          maxLength={selectedIso2 !== 'US' ? 15 : undefined}
                          numericOnly={selectedIso2 !== 'US'}
                          countryCode={
                            <CountryCodeSelect
                              value={selectedDialCode}
                              onChange={(dialCode) => {
                                setSelectedDialCode(dialCode);
                                setFieldValue(
                                  'phone',
                                  formatPhoneDisplayForCountry(
                                    values.phone,
                                    dialCode
                                  )
                                );
                              }}
                              disabled={isLoading || isSubmitting}
                            />
                          }
                          error={
                            (touched.phone && !!errors.phone) || !!errorMessage
                          }
                          errorMessage={touched.phone ? errors.phone : ''}
                          onChange={(e) => handlePhoneChange(e, handleChange)}
                          onPaste={(e) => handlePhonePaste(e, setFieldValue)}
                          onBlur={handleBlur}
                          value={values.phone}
                          name="phone"
                          disabled={isLoading || isSubmitting}
                          tabIndex={-1}
                          containerClassName="self-stretch"
                          inputClassName="rounded-xl"
                        />
                      </div>
                      {errorMessage && (
                        <div className="w-full font-geist font-normal text-[14px] leading-[24px] tracking-[-0.14px] flex justify-start items-start gap-2">
                          <span className="flex items-start gap-2">
                            <Icon
                              iconName="warningCircle"
                              iconProps={{
                                className: 'w-5 h-5 flex-shrink-0 mt-0.5',
                              }}
                            />
                            <span className="text-[#E5484D] ">
                              {errorMessage}{' '}
                              {isAccountRegistered && (
                                <span
                                  onClick={handleToggleModal}
                                  className="text-focus text-sm font-normal font-geist leading-6 hover:opacity-75 cursor-pointer h-auto p-0 underline"
                                >
                                  Login instead
                                </span>
                              )}
                            </span>
                          </span>
                        </div>
                      )}
                      <div className="self-stretch opacity-50 justify-start text-gray-1000 text-sm font-normal font-geist leading-6">
                        Standard carrier rates may apply. You consent to receive
                        messages from Zeme via SMS to verify your phone number.
                        Reply STOP to opt out.
                      </div>
                    </div>

                    {/* Error Messages - Mobile */}
                    {errorMessage === ERROR_MESSAGES.accountAlreadyExists && (
                      <div className="inline-flex justify-start items-start gap-2">
                        <Icon
                          iconName="warningCircle"
                          iconProps={{
                            className: 'w-5 h-5 flex-shrink-0 mt-0.5',
                          }}
                        />
                        <span className="font-geist font-normal text-[#E5484D] text-[14px] leading-[24px] tracking-[-0.14px]">
                          Looks like you already have an account.
                          {onOpenLoginModal && (
                            <>
                              {' '}
                              <Button
                                type="button"
                                variant="link"
                                onClick={() => {
                                  onOpenLoginModal();
                                }}
                                className="text-focus underline hover:opacity-85 h-auto p-0"
                              >
                                Want to log in instead?
                              </Button>
                            </>
                          )}
                        </span>
                      </div>
                    )}
                    {/* Continue Button - Mobile */}
                    <Button
                      type="button"
                      variant="primary"
                      size="xl"
                      className="self-stretch h-12 px-4 py-3 bg-focus rounded-[36px] inline-flex justify-center items-center gap-2"
                      disabled={
                        isLoading ||
                        isSubmitting ||
                        isGoogleLoading ||
                        isAppleLoading ||
                        !values.phone ||
                        !canContinueWithPhone(values.phone)
                      }
                      isLoading={
                        isLoading ||
                        isSubmitting ||
                        isGoogleLoading ||
                        isAppleLoading
                      }
                      loadingType="dots"
                      onClick={() =>
                        handleContinue(submitForm, isValid, values)
                      }
                    >
                      <div className="justify-start text-white text-base font-medium font-geist leading-7">
                        Continue
                      </div>
                      <Icon
                        iconName="arrowRight"
                        iconProps={{ className: 'w-5 h-5 text-white' }}
                      />
                    </Button>

                    {/* Social Login Buttons - Mobile Signup */}
                    {!hideOAuth && (
                      <>
                        <div className="self-stretch flex items-center gap-4">
                          <div className="flex-1 h-px bg-gray-300" />
                          <span className="text-gray-400 text-sm font-medium leading-none uppercase">
                            OR
                          </span>
                          <div className="flex-1 h-px bg-gray-300" />
                        </div>
                        <div className="flex flex-col gap-2 w-full">
                          <GoogleLoginButton
                            onSuccess={handleGoogleSuccess}
                            isLoading={isGoogleLoading}
                            disabled={isAppleLoading}
                          />
                          <AppleButton
                            onSuccess={(token, user) =>
                              handleAppleSuccess(token, user)
                            }
                            isLoading={isAppleLoading}
                            disabled={isGoogleLoading}
                          />
                        </div>
                      </>
                    )}

                    {/* Login Link - Mobile */}
                    <div className="w-48 justify-start text-focus text-sm font-normal font-geist leading-6">
                      <Button
                        type="button"
                        variant="link"
                        className="hover:opacity-85 h-auto p-0 !text-sm !text-focus"
                        onClick={handleToggleModal}
                      >
                        Have an account? Log in
                      </Button>
                    </div>
                  </div>
                )}

                {/* Desktop Content */}
                <div className="hidden md:flex self-stretch flex-col justify-start items-start gap-2 flex-1 min-h-0 w-full">
                  <Form className="self-stretch px-4 md:px-8 py-2.5 inline-flex flex-col justify-start items-start gap-6 md:gap-8 flex-1 min-h-0">
                    <div className="self-stretch flex flex-col justify-end items-center gap-2.5">
                      <div className="self-stretch inline-flex flex-col justify-start items-start gap-2">
                        <div className="self-stretch justify-start text-gray-800 text-sm font-normal font-geist leading-6">
                          {isLoginMode ? 'Login' : 'Create your account'}
                        </div>
                        <TextField
                          placeholder="Phone Number"
                          mask={phoneMask}
                          size="lg"
                          type="tel"
                          maxLength={selectedIso2 !== 'US' ? 15 : undefined}
                          numericOnly={selectedIso2 !== 'US'}
                          countryCode={
                            <CountryCodeSelect
                              value={selectedDialCode}
                              onChange={(dialCode) => {
                                setSelectedDialCode(dialCode);
                                setFieldValue(
                                  'phone',
                                  formatPhoneDisplayForCountry(
                                    values.phone,
                                    dialCode
                                  )
                                );
                              }}
                              disabled={isLoading || isSubmitting}
                            />
                          }
                          error={
                            (touched.phone && !!errors.phone) || !!errorMessage
                          }
                          errorMessage={touched.phone ? errors.phone : ''}
                          onChange={(e) => handlePhoneChange(e, handleChange)}
                          onPaste={(e) => handlePhonePaste(e, setFieldValue)}
                          onBlur={handleBlur}
                          onKeyDown={(e) => {
                            if (e?.key === 'Enter') {
                              handleContinue(submitForm, isValid, values);
                            }
                          }}
                          value={values.phone}
                          name="phone"
                          disabled={isLoading || isSubmitting}
                          tabIndex={-1}
                          containerClassName="self-stretch"
                          inputClassName="rounded-xl"
                        />
                      </div>
                      {errorMessage === ERROR_MESSAGES.accountNotFound && (
                        <div className="self-stretch inline-flex justify-start items-start gap-2">
                          <Icon
                            iconName="warningCircle"
                            iconProps={{
                              className: 'w-5 h-5 flex-shrink-0 mt-0.5',
                            }}
                          />
                          <span className="font-geist font-normal text-[#E5484D] text-[14px] leading-[24px] tracking-[-0.14px]">
                            This account doesn&apos;t exist.
                            {onOpenRegisterModal && (
                              <>
                                {' '}
                                <Button
                                  type="button"
                                  variant="link"
                                  onClick={() => {
                                    onOpenRegisterModal();
                                  }}
                                  className="text-focus underline hover:opacity-85 h-auto p-0"
                                >
                                  Sign Up Here
                                </Button>
                              </>
                            )}
                          </span>
                        </div>
                      )}
                      {errorMessage === ERROR_MESSAGES.accountAlreadyExists && (
                        <div className="self-stretch inline-flex justify-start items-start gap-2">
                          <Icon
                            iconName="warningCircle"
                            iconProps={{
                              className: 'w-5 h-5 flex-shrink-0 mt-0.5',
                            }}
                          />
                          <span className="font-geist font-normal text-[#E5484D] text-[14px] leading-[24px] tracking-[-0.14px]">
                            Looks like you already have an account.
                            {onOpenLoginModal && (
                              <>
                                {' '}
                                <Button
                                  type="button"
                                  variant="link"
                                  onClick={() => {
                                    onOpenLoginModal();
                                  }}
                                  className="text-focus underline hover:opacity-85 h-auto p-0"
                                >
                                  Want to log in instead?
                                </Button>
                              </>
                            )}
                          </span>
                        </div>
                      )}
                      {errorMessage &&
                        errorMessage !== 'account_not_found' &&
                        errorMessage !== 'account_already_exists' && (
                          <div className="font-geist font-normal self-stretch text-[14px] leading-[24px] tracking-[-0.14px] inline-flex justify-start items-start gap-2">
                            <Icon
                              iconName="warningCircle"
                              iconProps={{
                                className: 'w-5 h-5 flex-shrink-0 mt-0.5',
                              }}
                            />
                            <span className="text-[#E5484D] ">
                              {errorMessage}{' '}
                              {isAccountRegistered ? (
                                <span
                                  onClick={handleToggleModal}
                                  className="text-focus text-sm font-normal font-geist leading-6 hover:opacity-75 cursor-pointer h-auto p-0 underline"
                                >
                                  Login instead
                                </span>
                              ) : (
                                <span
                                  onClick={handleToggleModal}
                                  className="text-focus text-sm font-normal font-geist leading-6 hover:opacity-75 cursor-pointer h-auto p-0 underline"
                                >
                                  Sign up instead
                                </span>
                              )}
                            </span>
                          </div>
                        )}
                      <div className="self-stretch opacity-50 justify-start text-gray-1000 text-sm font-normal font-geist leading-6">
                        {isLoginMode
                          ? "We'll text you to confirm your number. Standard carrier rates may apply."
                          : 'Standard carrier rates may apply. you consent to receive messages from Zeme via SMS to verify your phone number. Reply STOP to opt out.'}
                      </div>
                    </div>
                    <Button
                      type="submit"
                      variant="primary"
                      size="xl"
                      className="self-stretch h-12 px-4 py-3 bg-focus rounded-[36px] inline-flex justify-center items-center gap-2"
                      disabled={
                        isLoading ||
                        isSubmitting ||
                        isGoogleLoading ||
                        isAppleLoading
                      }
                      isLoading={
                        isLoading ||
                        isSubmitting ||
                        isGoogleLoading ||
                        isAppleLoading
                      }
                      loadingType="dots"
                    >
                      <div className="justify-start text-white text-base font-medium font-geist leading-7">
                        Continue
                      </div>
                      <Icon
                        iconName="arrowRight"
                        iconProps={{ className: 'w-5 h-5 text-white' }}
                      />
                    </Button>
                    {!hideOAuth && (
                      <>
                        <div className="self-stretch flex items-center gap-4">
                          <div className="flex-1 h-px bg-gray-300" />
                          <span className="text-gray-400 text-sm font-medium leading-none uppercase">
                            OR
                          </span>
                          <div className="flex-1 h-px bg-gray-300" />
                        </div>

                        <div className="flex flex-col gap-2 w-full">
                          <GoogleLoginButton
                            onSuccess={handleGoogleSuccess}
                            isLoading={isGoogleLoading}
                            disabled={isAppleLoading}
                          />
                          <AppleButton
                            onSuccess={(token, user) =>
                              handleAppleSuccess(token, user)
                            }
                            isLoading={isAppleLoading}
                            disabled={isGoogleLoading}
                          />
                        </div>
                      </>
                    )}
                  </Form>
                </div>

                {/* Login Link - Desktop */}
                <div className="hidden md:flex self-stretch w-full px-4 md:px-8 justify-start items-center gap-24 py-2">
                  <Button
                    type="button"
                    variant="link"
                    className="text-focus text-sm font-normal font-geist leading-6 hover:opacity-85 cursor-pointer h-auto p-0"
                    onClick={handleToggleModal}
                  >
                    {isLoginMode
                      ? "Don't have an account? Sign up"
                      : 'Have an account? Log in'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </Formik>
      </div>
      {/* Right Section - Gradient Background */}
      <div className="hidden md:flex relative flex-col p-2.5 items-start gap-2.5 self-stretch shrink-0 rounded-r-2xl bg-background-200 overflow-hidden w-[550px] h-[790px]">
        <GradientPanelSvg
          className="absolute inset-0 w-full h-full"
          preserveAspectRatio="none"
        />
      </div>
    </div>
  );
};

export default PhoneStep;
